// Client feedback: notes a client pinned in feedback mode inside an app we
// built, arriving in batches (feedback-ingest). Comms → Open lists them in the
// same feed as email and Trello;
// "Send to agent queue" turns a batch into a task + spec job. Status changes,
// answers and replies flow back to the client's app through
// client_feedback_outbox (triggers in 20261030160000_client_feedback.sql).

import { supabase } from './supabase';
import { enqueueAiJob, type AiJob } from './aiJobs';
import { PROMPT_DEFAULTS, loadPromptTemplate, renderPrompt } from './prompts';
import type { BadgeTone } from '../components/ui/Badge';

export type FeedbackKind = 'bug' | 'change' | 'question';
export type FeedbackStatus = 'received' | 'in_progress' | 'preview' | 'live' | 'answered' | 'closed' | 'withdrawn';

export interface FeedbackSource {
  id: string;
  key: string;
  client_id: string;
  label: string;
  /** Where the work goes (client_feedback_sources): GitHub owner/name, its branch, Supabase refs, Netlify site name. */
  repo?: string | null;
  repo_branch?: string | null;
  db_ref?: string | null;
  preview_db_ref?: string | null;
  netlify_site?: string | null;
}

export interface FeedbackItem {
  id: string;
  batch_id: string;
  client_id: string;
  origin: 'app' | 'website';
  kind: FeedbackKind;
  message: string;
  sender_name: string | null;
  page_url: string | null;
  route: string | null;
  page_title: string | null;
  record: { type?: string; id?: string; refs?: string[] };
  record_context: unknown;
  element: { tag?: string; text?: string; selector?: string; heading?: string; context?: string; nearby?: string[] };
  click: { xPct?: number; yPct?: number; viewport?: { w: number; h: number } };
  errors: Array<{ at: string; message: string }>;
  network: Array<{ at: string; method: string; url: string; status: number; detail?: string }>;
  app_version: string | null;
  screenshot_path: string | null;
  crop_path: string | null;
  pinned_at: string | null;
  status: FeedbackStatus;
  answer: string | null;
  preview_url: string | null;
  live_url: string | null;
  status_note: string | null;
  withdrawn_at: string | null;
}

export interface FeedbackBatch {
  id: string;
  source_id: string;
  client_id: string;
  sender: { full_name?: string; email?: string; role?: string };
  note: string | null;
  app_url: string | null;
  sent_at: string | null;
  received_at: string;
  status: FeedbackStatus;
  ai_job_id: string | null;
  cleared_at: string | null;
  withdrawn_at: string | null;
  /** The job's client note as posted to the client's app (Submit Note on the job page). The latest post replaces the one before. */
  submission_note?: string | null;
  submission_note_at?: string | null;
  submission_note_by_name?: string | null;
  job?: { id: string; status: string } | null;
  source?: FeedbackSource | null;
  client?: { name: string } | null;
  items: FeedbackItem[];
}

export interface FeedbackMessage {
  id: string;
  item_id: string;
  author_name: string | null;
  from_bullfinch: boolean;
  body: string;
  created_at: string;
}

export const STATUS_LABEL: Record<FeedbackStatus, string> = {
  received: 'New',
  in_progress: 'In progress',
  preview: 'Preview ready',
  live: 'Live',
  answered: 'Answered',
  closed: 'Closed',
  withdrawn: 'Withdrawn by client',
};

/** Badge colour for each status, the same in the feed and the panel. */
export const STATUS_TONE: Record<FeedbackStatus, BadgeTone> = {
  received: 'accent',
  in_progress: 'warn',
  preview: 'info',
  live: 'up',
  answered: 'up',
  closed: 'idle',
  withdrawn: 'down',
};

export const KIND_LABEL: Record<FeedbackKind, string> = { bug: 'Something wrong', change: 'Change', question: 'Question' };

const BATCH_SELECT =
  '*, source:client_feedback_sources(id, key, client_id, label, repo, repo_branch, db_ref, preview_db_ref, netlify_site), client:clients(name), job:ai_jobs(id, status), items:client_feedback_items(*)';

const withSortedNotes = (rows: unknown) =>
  ((rows ?? []) as FeedbackBatch[]).map((b) => ({
    ...b,
    items: [...(b.items ?? [])].sort((a, c) => (a.pinned_at ?? '').localeCompare(c.pinned_at ?? '')),
  }));

/** Every batch not cleared, plus the 100 most recently cleared, for the Comms feed (its tabs sort them). */
export async function fetchFeedbackBatches(): Promise<FeedbackBatch[]> {
  const [open, cleared] = await Promise.all([
    supabase.from('client_feedback_batches').select(BATCH_SELECT).is('cleared_at', null).order('received_at', { ascending: false }).limit(300),
    supabase.from('client_feedback_batches').select(BATCH_SELECT).not('cleared_at', 'is', null).order('cleared_at', { ascending: false }).limit(100),
  ]);
  if (open.error) throw open.error;
  if (cleared.error) throw cleared.error;
  return [...withSortedNotes(open.data), ...withSortedNotes(cleared.data)];
}

/** The groups a queue job is working (normally one, after merging). Empty for a job that isn't client feedback. */
export async function fetchJobFeedback(jobId: string): Promise<FeedbackBatch[]> {
  const { data, error } = await supabase.from('client_feedback_batches').select(BATCH_SELECT).eq('ai_job_id', jobId);
  if (error) throw error;
  return withSortedNotes(data);
}

const DONE_JOB = ['complete', 'submitted', 'cancelled'];
const SETTLED_NOTE: FeedbackStatus[] = ['answered', 'closed', 'live', 'withdrawn'];

/**
 * Which Comms tab a batch sits in, by the same rules as a Trello card:
 * Open needs someone; Assigned is in the agent queue (its task); Cleared is
 * done (cleared by hand, the job marked Submitted, or nothing left to do).
 * A queued batch the client emptied comes back to Open: someone has to stop the job.
 */
export function feedbackTab(batch: FeedbackBatch): 'open' | 'assigned' | 'cleared' {
  if (batch.cleared_at) return 'cleared';
  if (batch.ai_job_id) {
    if (jobHasNoNotes(batch)) return 'open';
    return batch.job?.status === 'submitted' ? 'cleared' : 'assigned';
  }
  if (batch.status === 'withdrawn' || (batch.items.length > 0 && batch.items.every((i) => SETTLED_NOTE.includes(i.status)))) return 'cleared';
  return 'open';
}

/** Repo, branch and databases a source's work goes to, as "label: value" pairs (only the ones set). */
export function sourceWhere(source: FeedbackSource | null | undefined): Array<{ label: string; value: string }> {
  if (!source) return [];
  return [
    source.repo ? { label: 'Repo', value: `${source.repo}${source.repo_branch ? ` @ ${source.repo_branch}` : ''}` } : null,
    source.db_ref ? { label: 'Prod DB', value: source.db_ref } : null,
    source.preview_db_ref ? { label: 'Preview DB', value: source.preview_db_ref } : null,
    source.netlify_site ? { label: 'Netlify', value: source.netlify_site } : null,
  ].filter((x): x is { label: string; value: string } => !!x);
}

/** Feedback batches on the Comms Open tab, by the same rule the tab uses (feedbackTab), for the nav badge. */
export async function countOpenFeedback(): Promise<number> {
  const { data, error } = await supabase
    .from('client_feedback_batches')
    .select(BATCH_SELECT)
    .is('cleared_at', null)
    .limit(300);
  if (error) throw error;
  return withSortedNotes(data).filter((b) => feedbackTab(b) === 'open').length;
}

export async function shotUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  const { data } = await supabase.storage.from('client-feedback').createSignedUrl(path, 60 * 60);
  return data?.signedUrl ?? null;
}

export async function fetchMessages(itemId: string): Promise<FeedbackMessage[]> {
  const { data, error } = await supabase
    .from('client_feedback_messages')
    .select('id, item_id, author_name, from_bullfinch, body, created_at')
    .eq('item_id', itemId)
    .order('created_at');
  if (error) throw error;
  return (data ?? []) as FeedbackMessage[];
}

/** Push what the triggers queued now instead of on the next 5-minute tick. */
export async function flushFeedbackOutbox(): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;
  await fetch('/.netlify/functions/feedback-outbox', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: '{}',
  }).catch(() => undefined);
}

export async function replyToNote(itemId: string, me: { id: string; name: string }, body: string): Promise<void> {
  const { error } = await supabase
    .from('client_feedback_messages')
    .insert({ item_id: itemId, author_id: me.id, author_name: me.name, from_bullfinch: true, body: body.trim() });
  if (error) throw error;
  void flushFeedbackOutbox();
}

export async function answerNote(itemId: string, answer: string): Promise<void> {
  const { error } = await supabase
    .from('client_feedback_items')
    .update({ answer: answer.trim(), status: 'answered', updated_at: new Date().toISOString() })
    .eq('id', itemId);
  if (error) throw error;
  void flushFeedbackOutbox();
}

export async function setNoteStatus(itemId: string, status: FeedbackStatus, statusNote?: string): Promise<void> {
  const { error } = await supabase
    .from('client_feedback_items')
    .update({ status, ...(statusNote ? { status_note: statusNote } : {}), updated_at: new Date().toISOString() })
    .eq('id', itemId);
  if (error) throw error;
  void flushFeedbackOutbox();
}

/** Delete a note here and in the client's app (its batch goes too once empty). */
export async function deleteNote(itemId: string): Promise<void> {
  const { data, error } = await supabase.rpc('client_feedback_delete_item', { item: itemId });
  if (error) throw error;
  const shots = (data as string[] | null) ?? [];
  if (shots.length) await supabase.storage.from('client-feedback').remove(shots);
  void flushFeedbackOutbox();
}

/** A batch whose notes can still be merged into another group: New, not in the agent queue, not cleared. */
export const canMerge = (batch: FeedbackBatch) => !batch.ai_job_id && batch.status === 'received' && !batch.cleared_at;

/**
 * Merge these notes into one group (client_feedback_merge): the oldest of
 * their batches keeps going, the rest move into it, emptied batches go, and
 * the client's app is told to regroup the same way. All from one app, all New.
 */
export async function mergeNotes(itemIds: string[]): Promise<{ batch_id: string; moved: string[]; removed: string[] }> {
  const { data, error } = await supabase.rpc('client_feedback_merge', { items: itemIds });
  if (error) throw error;
  void flushFeedbackOutbox();
  return data as unknown as { batch_id: string; moved: string[]; removed: string[] };
}

export async function clearBatch(batchId: string, userId: string, clear = true): Promise<void> {
  const { error } = await supabase
    .from('client_feedback_batches')
    .update(clear ? { cleared_at: new Date().toISOString(), cleared_by: userId } : { cleared_at: null, cleared_by: null })
    .eq('id', batchId);
  if (error) throw error;
}

const one = (s: string | null | undefined, max = 300) => (s ?? '').replace(/\s+/g, ' ').trim().slice(0, max);

/** Notes the client hasn't taken back. Withdrawn ones never go to an agent. */
export const liveNotes = (batch: FeedbackBatch) => batch.items.filter((i) => i.status !== 'withdrawn');

/** A batch in the agent queue whose job is still open but whose notes the client all withdrew. */
export function jobHasNoNotes(batch: FeedbackBatch): boolean {
  if (!batch.ai_job_id || liveNotes(batch).length) return false;
  return !DONE_JOB.includes(batch.job?.status ?? '');
}

/** The notes, one block each, as the agent reads them. */
export function formatNotes(batch: FeedbackBatch): string {
  const notes = liveNotes(batch);
  return notes
    .map((it, i) => {
      const lines = [
        `NOTE ${i + 1} of ${notes.length} · ${KIND_LABEL[it.kind]} · note id ${it.id}`,
        `"${it.message.trim()}"`,
        `Where: ${it.origin === 'website' ? 'website' : 'app'} ${it.page_url ?? it.route ?? '(unknown page)'}${it.page_title ? ` — ${one(it.page_title, 120)}` : ''}`,
      ];
      if (it.record?.type || it.record?.refs?.length) {
        lines.push(`Record: ${[it.record.type && `${it.record.type} ${it.record.id ?? ''}`.trim(), it.record.refs?.length ? `on screen: ${it.record.refs.join(', ')}` : ''].filter(Boolean).join(' · ')}`);
      }
      if (it.element?.selector) {
        lines.push(`Pinned on: <${it.element.tag}> "${one(it.element.text, 120)}" (${it.element.selector})`);
        if (it.element.heading) lines.push(`Under heading: ${one(it.element.heading, 120)}`);
        if (it.element.nearby?.length) lines.push(`Nearby: ${it.element.nearby.slice(0, 8).join(' | ')}`);
        if (it.element.context) lines.push(`Block text: ${one(it.element.context, 300)}`);
      }
      if (it.click?.xPct != null) lines.push(`Pin at ${it.click.xPct}% across, ${it.click.yPct}% down a ${it.click.viewport?.w ?? '?'}x${it.click.viewport?.h ?? '?'} screen`);
      if (it.errors?.length) lines.push(`Errors before pinning: ${it.errors.slice(-3).map((e) => one(e.message, 160)).join(' || ')}`);
      if (it.network?.length) lines.push(`Failed requests: ${it.network.slice(-3).map((n) => `${n.method} ${n.url} → ${n.status}${n.detail ? ` ${one(n.detail, 120)}` : ''}`).join(' || ')}`);
      if (it.app_version) lines.push(`App version: ${it.app_version}`);
      if (it.screenshot_path) lines.push(`Screenshots (Cockpit storage, bucket client-feedback): ${it.screenshot_path}, ${it.crop_path ?? '-'}`);
      return lines.join('\n');
    })
    .join('\n\n');
}

/** The codebase/database block every feedback job carries, whatever the saved prompt template says. */
export function formatSourceWhere(source: FeedbackSource | null | undefined): string {
  const where = sourceWhere(source);
  if (!where.length) {
    return `CODEBASE AND DATABASE\nNot recorded for ${source?.label ?? 'this app'} (client_feedback_sources). Find the repo by a string the client can see, and name the database before any write.`;
  }
  return ['CODEBASE AND DATABASE', ...where.map((w) => `${w.label}: ${w.value}`)].join('\n');
}

export function formatFeedbackQueueNote(batch: FeedbackBatch, template: string = PROMPT_DEFAULTS['client-feedback']): string {
  return `${renderFeedbackPrompt(batch, template)}\n\n${formatSourceWhere(batch.source)}`;
}

function renderFeedbackPrompt(batch: FeedbackBatch, template: string): string {
  return renderPrompt(template, {
    client: batch.client?.name ?? 'the client',
    count: liveNotes(batch).length,
    sender: batch.sender?.full_name ?? 'the client',
    source: batch.source?.label ?? 'their app',
    batch_note: batch.note ? one(batch.note, 1000) : '',
    batch_id: batch.id,
    notes: formatNotes(batch),
  });
}

/** Task + spec job for a whole batch. The job's progress moves every note (ai_jobs_feedback_progress). */
export async function sendBatchToQueue(batch: FeedbackBatch, createdBy: { id: string; name: string }): Promise<AiJob> {
  if (batch.ai_job_id) throw new Error('This batch is already in the agent queue.');
  const notes = liveNotes(batch);
  if (!notes.length) throw new Error('The client withdrew every note in this batch.');
  const who = batch.sender?.full_name ?? 'Client';
  const title = `Feedback — ${batch.client?.name ?? 'client'}: ${notes.length} note${notes.length === 1 ? '' : 's'} from ${who}`;
  const template = await loadPromptTemplate('client-feedback');
  const queueNote = formatFeedbackQueueNote(batch, template);
  const now = new Date();
  const { data: task, error: taskError } = await supabase
    .from('client_tasks')
    .insert({
      client_id: batch.client_id,
      title,
      description: queueNote,
      status: 'In Progress',
      due_date: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12).toISOString(),
      assigned_by: createdBy.id,
      assigned_to: createdBy.id,
      assigned_to_name: createdBy.name,
    })
    .select('id')
    .single();
  if (taskError) throw taskError;
  const job = await enqueueAiJob({
    task_id: task.id,
    title,
    card_id: null,
    created_by: createdBy.id,
    queue_note: queueNote,
    kind: 'fulfillment',
    brief: {
      feedback_batch_id: batch.id,
      feedback_item_ids: notes.map((i) => i.id),
      feedback_source: batch.source
        ? {
            key: batch.source.key,
            repo: batch.source.repo ?? null,
            repo_branch: batch.source.repo_branch ?? null,
            db_ref: batch.source.db_ref ?? null,
            preview_db_ref: batch.source.preview_db_ref ?? null,
            netlify_site: batch.source.netlify_site ?? null,
          }
        : null,
    },
  });
  const { error } = await supabase.from('client_feedback_batches').update({ ai_job_id: job.id }).eq('id', batch.id);
  if (error) throw error;
  // Queuing the job moved every note to In progress (ai_jobs_feedback_progress); tell the client's app now.
  void flushFeedbackOutbox();
  return job;
}

/**
 * The client note as the client's app shows it: plain text. Markdown marks go
 * (bold, headings, links become "text (url)"), list bullets become "•".
 */
export function plainNote(markdown: string): string {
  return markdown
    .replace(/\r\n/g, '\n')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text: string, url: string) => (text === url ? url : `${text} (${url})`))
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '• ')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(^|[\s(])[*_]([^*_\n]+)[*_](?=[\s).,!?:;]|$)/gm, '$1$2')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export type SubmissionNoteState =
  | { kind: 'none' }
  | { kind: 'missing' }
  | { kind: 'changed'; at: string }
  | { kind: 'posted'; at: string };

/**
 * Has this job's client note reached the client's feedback? 'none' = the job
 * isn't client feedback (no button, no modal). 'missing' = never posted on at
 * least one of its groups; 'changed' = posted, but the note was edited since.
 */
export function submissionNoteState(batches: FeedbackBatch[], clientNote: string | null | undefined): SubmissionNoteState {
  const live = batches.filter((b) => b.status !== 'withdrawn');
  if (!live.length) return { kind: 'none' };
  if (live.some((b) => !b.submission_note || !b.submission_note_at)) return { kind: 'missing' };
  const at = live.map((b) => b.submission_note_at as string).sort()[0];
  const want = plainNote(clientNote ?? '');
  return live.every((b) => b.submission_note === want) ? { kind: 'posted', at } : { kind: 'changed', at };
}

/** Submit Note: the job's client note goes on every group the job works and to the client's app (replacing an earlier post). */
export async function postSubmissionNote(jobId: string, clientNote: string, author: string): Promise<{ batch_ids: string[]; posted_at: string }> {
  const { data, error } = await supabase.rpc('client_feedback_post_note', { job: jobId, body: plainNote(clientNote), author });
  if (error) throw error;
  void flushFeedbackOutbox();
  return data as unknown as { batch_ids: string[]; posted_at: string };
}

/** Search: client, sender, app and every note's words. */
export const feedbackMatches = (b: FeedbackBatch, q: string) =>
  [b.client?.name, b.sender?.full_name, b.source?.label, b.note, ...b.items.map((i) => i.message)]
    .some((s) => (s ?? '').toLowerCase().includes(q));

/** When a batch last did something worth sorting by: arrived, or the client withdrew from it. */
export const feedbackAt = (b: FeedbackBatch) => [b.received_at, b.withdrawn_at].filter(Boolean).sort().at(-1) as string;
